import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { agentById, countryByCode, liveQuotes, liveCountryCodes, liveAgentIds, liveParcelSpec } from "@/data";
import { heatColor } from "@/components/charts";
import { money } from "@/format";

export default function MatrixPage() {
  const [q, setQ] = useState("");

  // cheapest real CNY price per agent × country
  const { grid, colStats, countries, agents } = useMemo(() => {
    const grid = new Map<string, Map<string, number>>();
    for (const quote of liveQuotes) {
      const row = grid.get(quote.agentId) ?? new Map<string, number>();
      const cur = row.get(quote.countryCode);
      if (cur == null || quote.priceCny! < cur) row.set(quote.countryCode, quote.priceCny!);
      grid.set(quote.agentId, row);
    }
    const colStats = new Map<string, { min: number; max: number }>();
    for (const c of liveCountryCodes) {
      const vals: number[] = [];
      for (const a of liveAgentIds) { const v = grid.get(a)?.get(c); if (v != null) vals.push(v); }
      if (vals.length) colStats.set(c, { min: Math.min(...vals), max: Math.max(...vals) });
    }
    const needle = q.trim().toLowerCase();
    const agents = liveAgentIds.filter((a) => !needle || (agentById.get(a)?.name ?? a).toLowerCase().includes(needle));
    return { grid, colStats, countries: liveCountryCodes, agents };
  }, [q]);

  return (
    <div>
      <div className="row spread wrap" style={{ gap: 8 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>Country × agent price matrix</h1>
          <p className="muted" style={{ margin: 0 }}>
            Cheapest <strong>real</strong> line per agent and destination — captured live, greener = cheaper in that column.
          </p>
        </div>
        <span className="pill-live">📦 {(liveParcelSpec?.weightG ?? 5000) / 1000} kg · {liveParcelSpec?.lengthCm}×{liveParcelSpec?.widthCm}×{liveParcelSpec?.heightCm} cm · CNY</span>
      </div>

      <div className="card card-pad mt" style={{ padding: "10px 12px" }}>
        <div className="searchbar" style={{ maxWidth: 320 }}>
          <input className="input" placeholder="Filter agents…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="table-wrap mt sticky-1">
        <table className="data">
          <thead>
            <tr>
              <th>Agent</th>
              {countries.map((c) => (
                <th key={c} className="num" title={countryByCode.get(c)?.name}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a}>
                <td><Link to={`/agent/${a}`}>{agentById.get(a)?.name ?? a}</Link></td>
                {countries.map((c) => {
                  const v = grid.get(a)?.get(c);
                  const st = colStats.get(c);
                  const t = v != null && st && st.max > st.min ? 1 - (v - st.min) / (st.max - st.min) : v != null ? 0.6 : null;
                  return (
                    <td key={c} className="num" style={{ background: heatColor(t), fontSize: "0.78rem" }}>
                      {v == null ? "" : money(v, "CNY")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tiny faint mt">
        Cheapest line only; open the <Link to="/calculator">calculator</Link> for every line, ETA and value per destination.
        {" "}{agents.length} agents × {countries.length} destinations, all real.
      </p>
    </div>
  );
}
